<script>
import { mapState } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import LeaveModel from '@models/Leave';
import LeaveModal from '@views/leaves/edit-leave';
import HeaderComponent from '@components/HeaderComponent';
import { DateTime } from 'luxon';

export default {
  page() {
    return {
      title: this.$t('titles.leaves'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    LeaveModal,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'start_date',
      sortDesc: true,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        {
          key: 'start_date',
          label: this.$t('table_labels.start_date'),
          sortable: true,
          formatter: (value) => DateTime.fromSQL(value).toFormat('dd.MM.yyyy'),
        },
        {
          key: 'end_date',
          label: this.$t('table_labels.end_date'),
          sortable: true,
          formatter: (value) => DateTime.fromSQL(value).toFormat('dd.MM.yyyy'),
        },
        { key: 'amount_of_days', label: this.$t('table_labels.amount_of_days'), sortable: true },
        { key: 'confirmed', label: this.$t('table_labels.confirmed'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      selectedLeave: null,
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
    }),
  },
  methods: {
    editLeave(id) {
      this.selectedLeave = id;
    },
    itemsProvider(ctx) {
      return LeaveModel.orderBy(this.sortDir + ctx.sortBy)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.leave') }))) {
        this.$wait.start('leave processing');
        const leave = new LeaveModel({ id });
        await leave.delete();
        this.$wait.end('leave processing');
        this.$refs.asyncTable.refresh();
      }
    },
    exportPdf(id) {
      this.$wait.start(`downloading${id}`);
      this.$http
        .get(`leaves/export/${id}`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading${id}`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          let filename = `Leave#${id}`;
          const match = response.headers['content-disposition'].match(/filename="([^"]+)"/);

          if (match && match.length > 1) {
            filename = match[1];
          }

          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading${id}`));
    },
    onSubmit() {
      this.selectedLeave = null;
      this.$refs.asyncTable.refresh();
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent
          :title="$t('titles.leaves')"
          :button-text="$t('titles.create_leave')"
          @button-pressed="selectedLeave = 'new'"
        ></HeaderComponent>
      </b-col>
    </b-row>
    <div id="leaves-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('leave processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        :items="itemsProvider"
        stacked="md"
        striped
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
            <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
            <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
          </template>
        </template>
        <template v-slot:cell(confirmed)="row">
          <template v-if="row.item.confirmed">
            <FontAwesomeIcon :icon="['fas', 'check']" class="text-success mr-2" />
            <span v-if="row.item.confirmed_by && row.item.confirmed_at" class="near-label-info text-secondary" @click.stop>
              <FontAwesomeIcon :id="`confirmPopover${row.item.id}`" :icon="['fas', 'info-circle']" tabindex="-1" />
              <b-popover :target="`confirmPopover${row.item.id}`" triggers="focus" placement="rightbottom">
                {{ $t('infos.leave_confirmed_by', { name: row.item.confirmed_by.name, date: row.item.confirmed_at }) }}
              </b-popover>
            </span>
          </template>
          <template v-else>
            <FontAwesomeIcon :icon="['fas', 'times-circle']" class="text-danger" />
          </template>
        </template>
        <template v-slot:cell(actions)="row">
          <b-btn class="mr-2" variant="outline-primary" size="sm" :disabled="$wait.is(`downloading${row.item.id}`)" @click="exportPdf(row.item.id)">
            <FontAwesomeIcon :icon="['fas', 'file-export']" />
            <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}`)" class="ml-1" icon="spinner" spin />
          </b-btn>
          <b-btn v-if="!row.item.confirmed" class="mr-2" size="sm" variant="outline-primary" @click="editLeave(row.item.id)">
            <FontAwesomeIcon :icon="['fas', 'edit']" />
          </b-btn>
          <b-btn v-if="!row.item.confirmed" size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
            <FontAwesomeIcon :icon="['fas', 'times-circle']" />
          </b-btn>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
    <LeaveModal v-model="selectedLeave" :loaded-id="selectedLeave" @updated="onSubmit" @hidden="selectedLeave = null" />
  </Layout>
</template>
